/* ────────────────────────────────────────────────────────────────
   src/components/WhoWeAre.jsx
────────────────────────────────────────────────────────────────── */
import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Avatar,
  useTheme,
} from "@mui/material";
import { FiCheck } from "react-icons/fi";
import { motion } from "framer-motion";
import whoImage from "../assets/solar-team.jpg";

const BENEFITS = [
  "Ahorro significativo en el recibo de luz.",
  "Contribución al medio ambiente y sostenibilidad.",
  "Aumento del valor de la propiedad.",
  "Independencia energética.",
  "Garantías y soporte post-venta.",
];

export default function WhoWeAre() {
  const theme = useTheme();
  const glow = `0 0 6px ${theme.palette.primary.main}55`;

  return (
    <Box
      component={motion.section}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        width: "100%",
        bgcolor: "#fff",
        borderRadius: { xs: 0, md: 4 }, // sin radio en móvil
        overflow: { xs: "visible", md: "hidden" },
        boxShadow: theme.shadows[3],
      }}
    >
      {/* Imagen */}
      <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 50%" } }}>
        <Box
          component="img"
          src={whoImage}
          alt="Equipo KW-Solar"
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>

      {/* Texto */}
      <Box
        sx={{
          flex: { xs: "1 1 100%", md: "1 1 50%" },
          p: { xs: 3, md: 6 },
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Typography
          variant="h3"
          fontFamily='"Ethos Nova", sans-serif'
          fontWeight={900}
          color="primary.main"
          sx={{
            "&::after": {
              content: '""',
              display: "block",
              width: 64,
              height: 4,
              mt: 1,
              borderRadius: 2,
              bgcolor: theme.palette.primary.main,
            },
          }}
        >
          ¿Quiénes somos?
        </Typography>

        <Typography sx={{ lineHeight: 1.7 }}>
          En&nbsp;
          <Box component="span" sx={{ fontWeight: 700, fontStyle: "italic" }}>
            KW Solar Energy
          </Box>{" "}
          ayudamos a las familias y negocios de Hermosillo a reducir hasta un
          <strong>&nbsp;95 %</strong> su recibo de luz. Gestionamos diseño,
          instalación y trámites ante CFE con tecnología Tier-1 y financiamiento
          accesible.
        </Typography>

        <Typography fontWeight={700}>Tu ahorro empieza hoy.</Typography>

        <List dense sx={{ pl: 0 }}>
          {BENEFITS.map((b) => (
            <ListItem key={b} disableGutters sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    width: 26,
                    height: 26,
                    fontSize: 14,
                    boxShadow: glow,
                  }}
                >
                  <FiCheck size={14} color="#fff" />
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={b}
                primaryTypographyProps={{ sx: { fontWeight: 500 } }}
              />
            </ListItem>
          ))}
        </List>

        <Button
          href="#contacto"
          variant="contained"
          size="large"
          sx={{ alignSelf: "flex-start", px: 5, textTransform: "none" }}
        >
          Cotiza con nosotros
        </Button>
      </Box>
    </Box>
  );
}
